import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { CLIENT_IDS_STORAGE_KEY } from "../../helpers/constant";
import { hideChatBot } from "./redux/chatBotSlice";
import { useDockAnimation } from "./useDockAnimation";
import { buildCompactRect, resolveLauncherRect } from "./dockAnimation";

const IFRAME_SIZES = {
  compact: {
    position: "fixed",
    right: 0,
    bottom: 0,
    width: 370,
    height: 85,
    border: 0,
    zIndex: 1000
  },
  default: {
    position: "fixed",
    right: 0,
    bottom: 0,
    width: 500,
    height: 620,
    border: 0,
    zIndex: 1000
  },
  fullscreen: {
    position: "fixed",
    inset: 0,
    width: "99vw",
    height: "100vh",
    border: 0,
    zIndex: 9999
  }
};

const VALID_SIZES = new Set(Object.keys(IFRAME_SIZES));
const DRAG_THRESHOLD = 4;

export function normalizeClientIds(clientIds) {
  return (clientIds ?? "")
    .split(",")
    .map((clientId) => clientId.trim())
    .filter(Boolean);
}

export function persistSsoClientIds(clientIds) {
  const normalizedClientIds = normalizeClientIds(clientIds).join(",");

  if (!normalizedClientIds) {
    sessionStorage.removeItem(CLIENT_IDS_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(CLIENT_IDS_STORAGE_KEY, normalizedClientIds);
}

export function hasClientIdAccess(clientIds, targetClientId) {
  const normalizedTargetClientId = targetClientId?.trim();

  if (!normalizedTargetClientId) {
    return false;
  }

  return normalizeClientIds(clientIds).includes(normalizedTargetClientId);
}

export function canShowChatBot() {
  return (
    Boolean(process.env.REACT_APP_CHAT_BOT_URL) &&
    hasClientIdAccess(
      sessionStorage.getItem(CLIENT_IDS_STORAGE_KEY),
      process.env.REACT_APP_CHAT_BOT_CLIENT_ID
    )
  );
}

function clampCompactPosition(left, top) {
  if (typeof window === "undefined") return { left, top };

  const maxLeft = Math.max(0, window.innerWidth - IFRAME_SIZES.compact.width);
  const maxTop = Math.max(0, window.innerHeight - IFRAME_SIZES.compact.height);

  return {
    left: Math.min(Math.max(left, 0), maxLeft),
    top: Math.min(Math.max(top, 0), maxTop)
  };
}

function getDefaultCompactPosition() {
  if (typeof window === "undefined") return null;

  const left =
    window.innerWidth - IFRAME_SIZES.compact.right - IFRAME_SIZES.compact.width;
  const COMPACT_BOTTOM_OFFSET = 140;
  const top =
    window.innerHeight -
    IFRAME_SIZES.compact.bottom -
    IFRAME_SIZES.compact.height -
    COMPACT_BOTTOM_OFFSET;

  return clampCompactPosition(left, top);
}

function useEmbeddedIframeSizing({ allowedOrigin, defaultSize }) {
  const [size, setSize] = useState(defaultSize);
  const normalizedAllowedOrigin = useMemo(
    () => resolvePostMessageOrigin({ allowedOrigin }),
    [allowedOrigin]
  );

  useEffect(() => {
    const onMessage = (event) => {
      if (
        normalizedAllowedOrigin !== "*" &&
        event.origin !== normalizedAllowedOrigin
      ) {
        return;
      }
      if (event.data?.type !== "chat:size") return;

      const next = event.data?.value;
      if (VALID_SIZES.has(next)) {
        setSize(next);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [normalizedAllowedOrigin]);

  return { size, sizes: IFRAME_SIZES };
}

function resolvePostMessageOrigin({ allowedOrigin, src }) {
  if (typeof window === "undefined") return "*";

  if (allowedOrigin) {
    try {
      return new URL(allowedOrigin, window.location.origin).origin;
    } catch (e) {
      // Fallback to iframe src below.
    }
  }

  if (src) {
    try {
      return new URL(src, window.location.origin).origin;
    } catch (e) {
      // Fall through to wildcard.
    }
  }

  return "*";
}

function EmbeddedIframe({
  src,
  title,
  parentOrigin,
  allowedOrigin,
  defaultSize = "compact",
  allow = "clipboard-read; clipboard-write"
}) {
  const [token, setToken] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [compactPosition, setCompactPosition] = useState(() =>
    getDefaultCompactPosition()
  );

  const iframeRef = useRef(null);
  const dragOverlayRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const dragStateRef = useRef({
    isPointerDown: false,
    isDragging: false,
    skipNextClick: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0
  });
  const sizeRef = useRef(defaultSize);
  const dispatch = useDispatch();
  const loginStatus = useSelector((state) => state.auth.status);
  const isHidden = useSelector((state) => state.chatBot?.isHidden);
  const launcherRect = useSelector((state) => state.chatBot?.launcherRect);
  const tokenGPMS = localStorage.getItem("accessToken");

  useEffect(() => {
    if (loginStatus !== "succeeded") return;
    if (!tokenGPMS) return;
    setToken(tokenGPMS);
  }, [loginStatus, tokenGPMS]);

  const { size, sizes } = useEmbeddedIframeSizing({
    allowedOrigin,
    defaultSize
  });

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  const resolvedParentOrigin = useMemo(() => {
    if (typeof window === "undefined") return parentOrigin || "";

    if (!parentOrigin) return window.location.origin;

    try {
      return new URL(parentOrigin, window.location.origin).origin;
    } catch (e) {
      return parentOrigin;
    }
  }, [parentOrigin]);

  const resolvedSrc = useMemo(() => {
    let currentSrc = src;

    // Append Token
    if (token) {
      const separator = currentSrc.includes("?") ? "&" : "?";
      currentSrc = `${currentSrc}${separator}token=${
        process.env.REACT_APP_HOST_NAME
      }:${encodeURIComponent(token)}`;
    }

    if (!resolvedParentOrigin) return currentSrc;

    try {
      const url = new URL(currentSrc, window.location.origin);
      url.searchParams.set("parentOrigin", resolvedParentOrigin);
      return url.toString();
    } catch (e) {
      return currentSrc;
    }
  }, [resolvedParentOrigin, src, token]);

  const clearDragState = useCallback(() => {
    const dragState = dragStateRef.current;
    if (!dragState.isPointerDown && !dragState.isDragging) return;

    const dragOverlay = dragOverlayRef.current;
    if (dragOverlay?.releasePointerCapture && dragState.pointerId !== null) {
      try {
        dragOverlay.releasePointerCapture(dragState.pointerId);
      } catch (e) {
        // Ignore capture release failures.
      }
    }

    dragState.isPointerDown = false;
    dragState.isDragging = false;
    dragState.pointerId = null;
  }, []);

  const handlePointerMove = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState.isPointerDown || sizeRef.current !== "compact") return;

    const distanceX = event.clientX - dragState.startX;
    const distanceY = event.clientY - dragState.startY;

    if (
      !dragState.isDragging &&
      Math.abs(distanceX) < DRAG_THRESHOLD &&
      Math.abs(distanceY) < DRAG_THRESHOLD
    ) {
      return;
    }

    if (!dragState.isDragging) {
      dragState.isDragging = true;
      dragState.skipNextClick = true;
      const dragOverlay = dragOverlayRef.current;
      if (dragOverlay?.setPointerCapture && dragState.pointerId !== null) {
        try {
          dragOverlay.setPointerCapture(dragState.pointerId);
        } catch (e) {
          // Ignore capture failures for browser/iframe combinations that block it.
        }
      }
    }

    const nextLeft = event.clientX - dragState.offsetX;
    const nextTop = event.clientY - dragState.offsetY;
    setCompactPosition(clampCompactPosition(nextLeft, nextTop));
  }, []);

  const handlePointerUp = useCallback(() => {
    const dragState = dragStateRef.current;
    if (!dragState.isPointerDown) return;
    clearDragState();
  }, [clearDragState]);

  const handlePointerCancel = useCallback(() => {
    clearDragState();
  }, [clearDragState]);

  const handlePointerDown = useCallback(
    (event) => {
      if (size !== "compact" || event.button !== 0) return;

      const dragOverlay = dragOverlayRef.current;
      if (!dragOverlay) return;

      clearDragState();

      const rect = dragOverlay.getBoundingClientRect();
      dragStateRef.current.isPointerDown = true;
      dragStateRef.current.isDragging = false;
      dragStateRef.current.skipNextClick = false;
      dragStateRef.current.pointerId = event.pointerId;
      dragStateRef.current.startX = event.clientX;
      dragStateRef.current.startY = event.clientY;
      dragStateRef.current.offsetX = event.clientX - rect.left;
      dragStateRef.current.offsetY = event.clientY - rect.top;
    },
    [clearDragState, size]
  );

  const requestChatSizeChange = useCallback(
    (nextSize) => {
      if (!VALID_SIZES.has(nextSize)) return;

      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;

      const targetOrigin = resolvePostMessageOrigin({
        allowedOrigin,
        src: resolvedSrc
      });

      iframeWindow.postMessage(
        {
          type: "chat:size",
          value: nextSize,
          source: "parent"
        },
        targetOrigin
      );
    },
    [allowedOrigin, resolvedSrc]
  );

  const handleOverlayClick = useCallback(() => {
    const dragState = dragStateRef.current;
    if (size !== "compact") return;

    if (dragState.skipNextClick) {
      dragState.skipNextClick = false;
      return;
    }

    requestChatSizeChange("default");
  }, [requestChatSizeChange, size]);

  const handleClose = useCallback(
    (event) => {
      event.stopPropagation();
      clearDragState();
      dispatch(hideChatBot());
    },
    [clearDragState, dispatch]
  );

  useEffect(() => {
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
      clearDragState();
    };
  }, [clearDragState, handlePointerCancel, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (size !== "compact") {
      clearDragState();
      setCompactPosition(getDefaultCompactPosition());
      return;
    }

    setCompactPosition((current) => {
      if (!current) return getDefaultCompactPosition();
      return clampCompactPosition(current.left, current.top);
    });
  }, [clearDragState, size]);

  useEffect(() => {
    const handleResize = () => {
      setCompactPosition((current) => {
        if (!current) return getDefaultCompactPosition();
        return clampCompactPosition(current.left, current.top);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fly the widget to/from the navbar launcher when its visibility toggles.
  const { displayHidden, animStyle } = useDockAnimation({
    isHidden,
    resolveTarget: () => resolveLauncherRect(launcherRect),
    resolveSource: (hidden) =>
      buildCompactRect(
        // Closing: from its current spot (it may have been dragged).
        // Opening: always from the original default position.
        hidden ? compactPosition || getDefaultCompactPosition() : getDefaultCompactPosition(),
        IFRAME_SIZES.compact
      ),
    // Re-anchor the resting position to default before opening (avoids a flash).
    onBeforeOpen: () => setCompactPosition(getDefaultCompactPosition()),
    // Once docked back in, auto-open the full chat interface.
    onAfterOpen: () => requestChatSizeChange("default")
  });

  const containerStyle = useMemo(() => {
    const hiddenStyle = displayHidden ? { display: "none" } : null;
    const baseStyle = sizes[size] || sizes[defaultSize];
    if (size !== "compact")
      return { ...baseStyle, ...hiddenStyle, ...animStyle };

    const nextPosition = compactPosition || getDefaultCompactPosition();
    if (!nextPosition) return { ...baseStyle, ...hiddenStyle, ...animStyle };

    return {
      ...baseStyle,
      left: nextPosition.left,
      top: nextPosition.top,
      right: "auto",
      bottom: "auto",
      touchAction: "none",
      overflow: "hidden",
      ...hiddenStyle,
      ...animStyle
    };
  }, [compactPosition, defaultSize, size, sizes, displayHidden, animStyle]);

  const iframeStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      border: 0,
      display: "block"
    }),
    []
  );

  const dragOverlayStyle = useMemo(
    () => ({
      position: "absolute",
      inset: 0,
      zIndex: 1,
      cursor: "grab",
      touchAction: "none",
      background: "transparent"
    }),
    []
  );

  const closeButtonStyle = useMemo(
    () => ({
      position: "absolute",
      top: 6,
      right: 6,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      padding: 0,
      border: "none",
      borderRadius: "50%",
      background: "rgba(0, 0, 0, 0.45)",
      cursor: "pointer",
      lineHeight: 0
    }),
    []
  );

  useEffect(() => {
    // Reset error state whenever iframe source changes.
    setHasError(false);

    // If the iframe never fires a load event (e.g., blocked by X-Frame-Options),
    // hide it after a short grace period to avoid a grey box.
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    loadTimeoutRef.current = setTimeout(() => setHasError(true), 8000);

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [resolvedSrc]);

  const handleLoad = () => {
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    setHasError(false);
  };

  const handleError = () => {
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    setHasError(true);
  };

  if (!token || hasError) return null;
  return (
    <div style={containerStyle}>
      {size === "compact" && (
        <>
          <div
            ref={dragOverlayRef}
            style={dragOverlayStyle}
            onPointerDown={handlePointerDown}
            onClick={handleOverlayClick}
          />
          <button
            type="button"
            aria-label="Đóng chatbot"
            style={closeButtonStyle}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleClose}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 2L10 10M10 2L2 10"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </>
      )}
      <iframe
        key={token}
        ref={iframeRef}
        title={title}
        src={resolvedSrc}
        style={iframeStyle}
        allow={allow}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}

export default EmbeddedIframe;
